import { useEffect, useMemo, useRef } from 'react'
import { debounce } from 'lodash'
import { useApolloClient } from '@apollo/client'
import { isSuccess, useFirebase } from 'src/firebase/useFirebase'
import { useSessionUID } from 'src/session/useSession'
import {
  GetAdvancedCaseloadParticipantsDocument,
  GetAdvancedCaseloadParticipantsQuery,
  GetAdvancedCaseloadParticipantsQueryVariables,
} from './advancedCaseload.generated'

// uid -> server timestamp of the patient's last indicator-relevant change, written by `server`'s `signalCareTeam`.
type CoachSignals = Record<string, number>

const REFRESH_DEBOUNCE_MS = 500

// A coach who has never been signalled has no node, which reads the same as an empty map.
const NO_SIGNALS: CoachSignals = {}

// A local copy: Standard's lives beside a query document that would load `searchParticipants.generated` in every app-bar test (ADR-212).
const changedUids = (previous: CoachSignals, next: CoachSignals) =>
  Object.keys(next).filter((uid) => previous[uid] !== next[uid])

// The signals Standard's `useLiveParticipantIndicators` reads; the first snapshot is only the baseline (ADR-212).
export const useCaseloadIndicatorSignals = (uids: string[]) => {
  const client = useApolloClient()
  const coachID = useSessionUID()
  const request = useFirebase<CoachSignals>(coachID ? `subscriptions/participantIndicators/${coachID}` : null)
  const signals = isSuccess(request) ? request.data ?? NO_SIGNALS : undefined

  const caseload = useRef(uids)
  caseload.current = uids

  // The cache keys patients by uid, so re-reading only the signalled ones updates the caseload's rows.
  const pending = useRef(new Set<string>())
  const refreshSoon = useMemo(() => {
    const refresh = (batch: string[]) =>
      client.query<GetAdvancedCaseloadParticipantsQuery, GetAdvancedCaseloadParticipantsQueryVariables>({
        query: GetAdvancedCaseloadParticipantsDocument,
        variables: { uids: batch },
        fetchPolicy: 'network-only',
      })

    return debounce(() => {
      const batch = [...pending.current]
      pending.current.clear()
      // One refused patient denies the whole batch, so retry each alone to still refresh the rest, as Standard does.
      refresh(batch).catch(() => {
        if (batch.length > 1) {
          batch.forEach((uid) => refresh([uid]).catch(() => undefined))
        }
      })
    }, REFRESH_DEBOUNCE_MS)
  }, [client])
  useEffect(() => () => refreshSoon.cancel(), [refreshSoon])

  const lastSeen = useRef<CoachSignals | undefined>(undefined)
  useEffect(() => {
    if (signals === undefined) {
      return
    }

    const previous = lastSeen.current
    lastSeen.current = signals
    const changed = previous ? changedUids(previous, signals).filter((uid) => caseload.current.includes(uid)) : []
    if (changed.length > 0) {
      changed.forEach((uid) => pending.current.add(uid))
      refreshSoon()
    }
  }, [signals, refreshSoon])
}
